/* eslint-disable react/prop-types */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { toast } from 'react-toastify';
import { apipostFormData } from '../../service/api';

const ImportCsvModal = ({ open, handleClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setFile(null);
    setSummary(null);
    setUploading(false);
  };

  const closeAndReset = () => {
    reset();
    handleClose();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setSummary(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please choose a CSV file first.');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const result = await apipostFormData('bankrate-lead/import-csv', formData);
    setUploading(false);
    if (result?.status === 200 || result?.status === 201) {
      setSummary(result.data?.result || result.data || {});
      onImported?.();
    }
  };

  return (
    <Dialog open={open} onClose={closeAndReset} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Import Bankrate Leads (CSV)</Typography>
        <ClearIcon onClick={closeAndReset} style={{ cursor: 'pointer' }} />
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Upload a CSV file matching the Bankrate field spec. Rows are upserted
          by <code>leadId</code>; duplicates will update the existing record.
          Unassigned rows receive a loan officer via round-robin on insert.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            Choose CSV
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {file.name} — {Math.round(file.size / 1024)} KB
            </Typography>
          )}
        </Stack>

        {summary && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Import Summary
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Inserted
                </Typography>
                <Typography variant="h6" color="success.main">
                  {summary.inserted ?? 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Updated
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {summary.updated ?? 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Skipped
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {summary.skipped ?? 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Errors
                </Typography>
                <Typography variant="h6" color="error.main">
                  {summary.errors?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            {Array.isArray(summary.errors) && summary.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Some rows were skipped due to validation errors:
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {summary.errors.slice(0, 50).map((err, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ListItem key={idx} sx={{ py: 0 }}>
                      <ListItemText
                        primary={`Row ${err.row ?? idx + 1}: ${err.message || err.error || 'Unknown error'}`}
                        secondary={err.leadId ? `leadId: ${err.leadId}` : null}
                      />
                    </ListItem>
                  ))}
                  {summary.errors.length > 50 && (
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary={`...and ${summary.errors.length - 50} more`} />
                    </ListItem>
                  )}
                </List>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={closeAndReset} disabled={uploading}>
          {summary ? 'Close' : 'Cancel'}
        </Button>
        {!summary && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!file || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCsvModal;
